import { useState, useEffect } from 'react'
import config from 'isomorphic-config'
import pclnRequest from '@pcln/request'
import { AxiosError, AxiosResponse } from 'axios'
import { GraphQLResponse } from '@/types'
import { SearchProductData } from '@/components/RecentSearchCards/types'
import analytics from '@/shared-utils/analytics'
import getCookieByName from '@/shared-utils/get-cookie-by-name'

type RecentSearchesResults = {
  customerSearches: {
    searchProductData: [SearchProductData]
  }
}

type ApiMetadata = {
  appName: string
  appVersion: string
}

type SearchProductList = 'STAY, FLY, DRIVE' | 'STAY'

const useRecentSearchCard = (
  cguid: string,
  apiMetadata: ApiMetadata,
  searchProducts: SearchProductList = 'STAY, FLY, DRIVE'
) => {
  const [recentSearches, setRecentSearches] = useState<SearchProductData[]>([])
  useEffect(() => {
    const searchProductArray = searchProducts
      .split(',')
      .map(value => value.trim())
    async function fetchRecentSearches() {
      const { url, timeout } = config.client['pcln-graph']
      const options = {
        url: `${url}?gqlOp=customerSearches`,
        timeout,
        method: 'post',
        headers: {
          authToken: getCookieByName('dmc'),
          'apollographql-client-name': apiMetadata.appName,
          'apollographql-client-version': apiMetadata.appVersion
        },
        data: {
          query: `
          query CustomerSearches($cguid: ID!, $searchProducts: [SearchProductEnum]!) {
            customerSearches(cguid: $cguid, customerSearchInput: {
              rankType: RANKING_LAST_SEARCH
              searchProductType: $searchProducts
            }) {
              ... on SearchResults {
                searchProductData {
                  ... on StaySearchData {
                    productType
                    stayRoomCount
                    stayDestinationData {
                      destinationCityData {
                        cityID
                        cityName
                      }
                      startDate
                      endDate
                    }
                  }
                  ... on FlySearchData {
                    productType
                    flyCabinClass
                    flyTripType
                    flyOriginDestinationData {
                      originDestinationData {
                        originCityData {
                          cityName
                        }
                        destinationCityData {
                          cityName
                        }
                        originAirportData {
                          airportCode
                        }
                        destinationAirportData {
                          airportCode
                        }
                      }
                      startDate
                    }
                  }
                  ... on DriveSearchData {
                    productType
                    driveTripType
                    driveStartDate
                    driveEndDate
                    drivePickupTime
                    driveDropOffTime
                    driveOriginDestinationData {
                      originCityData {
                        cityName
                      }
                      originAirportData {
                        airportCode
                        airportName
                      }
                      destinationAirportData {
                        airportCode
                        airportName
                      }
                      destinationCityData {
                        cityName
                      }
                    }
                  }
                }
              }
              ... on SearchError {
                searchError
              }
            }
          }
          `,
          variables: {
            cguid,
            searchProducts: searchProductArray
          }
        }
      }

      try {
        const response: AxiosResponse<GraphQLResponse<RecentSearchesResults>> =
          await pclnRequest(options)
        const { errors, data } = response.data
        if (Array.isArray(errors) && errors.length > 0) {
          analytics.logError({
            message: `Could not complete Recent Search Card request.  Error message: ${errors[0].message}`
          })
        }
        setRecentSearches(data?.customerSearches?.searchProductData ?? [])
      } catch (e) {
        const error = e as AxiosError
        analytics.logError({
          message: `Error getting Recent Search Card request data. Error Message: ${error.message} Error Name: ${error.name}`
        })
      }
    }
    void fetchRecentSearches()
  }, [apiMetadata.appName, apiMetadata.appVersion, cguid, searchProducts])
  return recentSearches
}

export default useRecentSearchCard
